import React, { useState, useMemo, useEffect, useRef } from "react";
import { Grid, Box, Typography, FormControl, InputLabel, Select, MenuItem, Card, Dialog, DialogContent } from "@material-ui/core";
import * as XLSX from "xlsx";

import { useResumen } from "../hooks/useResumen";
import { useTablaResumen } from "../hooks/useTablaResumen";
import { getCompetenciasAlertas } from "../services/alertasGenericasService";
import TareasTerminadas from "../../general/TareasTerminadas";
import emma_w from "../../../global/imagenes/emma_w.png";

import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import BuildIcon from "@material-ui/icons/Build";
import GavelIcon from "@material-ui/icons/Gavel";

import TarjetasResumen from "../components/Resumen/TarjetasResumen";
import GraficaResumen from "../components/Resumen/GraficaResumen";
import GraficaComparativa from "../components/Resumen/GraficaComparativaNuevo";
import TablaResumen from "../components/Resumen/TablaResumen";
import FiltrosResumen from "../components/Resumen/FiltrosResumen";
import {
  FILTRO_TODOS,
  esFiltroTodos,
  getExcelResumen,
  getProgramasConResultadosPruebas,
} from "../services/alertasGenericasService";
import AppBarModal from "../../general/AppBarModal";
import { codigosSubcompetencia, etiquetaSubcompetencia, promedioSubcompetencia, valorNormalizado } from "../utils/subcompetencias";
import { truncarNota } from "../utils/formato";

const normalize = (text) => {
  if (!text) return "";
  return text
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
};

const coincidePeriodo = (item, anio, semestre) => {
  if (!esFiltroTodos(anio) && String(item.anio) !== String(anio)) return false;
  if (!esFiltroTodos(semestre) && String(item.semestre) !== String(semestre)) return false;
  return true;
};

const coincidePrograma = (item, programa) => {
  if (esFiltroTodos(programa)) return true;
  return normalize(item.programa || item.programa_nombre) === normalize(programa);
};

const PROGRAMA_DEFAULT = "Ingeniería de sistemas";

const Resumen = () => {
  const [filtroAnio, setFiltroAnio] = useState(FILTRO_TODOS);
  const [filtroSemestre, setFiltroSemestre] = useState(FILTRO_TODOS);
  const [filtroPrograma, setFiltroPrograma] = useState(PROGRAMA_DEFAULT);
  const [filtroCompetencia, setFiltroCompetencia] = useState("RC");
  const [opcionesCompetencia, setOpcionesCompetencia] = useState([]);
  const [descargandoExcel, setDescargandoExcel] = useState(false);
  const descargaExcelEnCurso = useRef(false);
  const [programasConPruebas, setProgramasConPruebas] = useState([]);
  const { datos, loading: loadingResumen } = useResumen(filtroCompetencia);

  const iconMap = {
    C1: <EmojiObjectsIcon style={{ fontSize: 18, color: "#0f172a" }} />,
    C2: <BuildIcon style={{ fontSize: 18, color: "#2563eb" }} />,
    C3: <GavelIcon style={{ fontSize: 18, color: "#16a34a" }} />,
  };
  // Competencias con más de 3 subcompetencias (CC llega a C7) no tienen un
  // icono propio asignado: se rota el mismo set de íconos en vez de dejarlas sin ícono.
  const iconosCiclo = [iconMap.C1, iconMap.C2, iconMap.C3];
  const iconoParaCodigo = (codigo, index) => iconMap[codigo] || iconosCiclo[index % iconosCiclo.length];

  const opcionesAnio = useMemo(() => {
    if (!Array.isArray(datos)) return [];
    return [...new Set(datos.map(item => Number(item.anio) || 0))]
      .sort((a, b) => a - b)
      .map(String);
  }, [datos]);

  const opcionesSemestre = useMemo(() => {
    if (!Array.isArray(datos)) return [];
    const datosFiltrados = esFiltroTodos(filtroAnio)
      ? datos
      : datos.filter((item) => String(item.anio) === String(filtroAnio));
    return [...new Set(datosFiltrados.map((item) => Number(item.semestre) || 0))]
      .filter(Boolean)
      .sort((a, b) => a - b)
      .map(String);
  }, [datos, filtroAnio]);

  useEffect(() => {
    let activo = true;
    getCompetenciasAlertas().then((lista) => {
      if (!activo) return;
      setOpcionesCompetencia(lista);
    });
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    const params = { competencia: filtroCompetencia };
    if (!esFiltroTodos(filtroAnio)) params.anio = filtroAnio;
    if (!esFiltroTodos(filtroSemestre)) params.semestre = filtroSemestre;
    getProgramasConResultadosPruebas(params).then((list) => {
      if (!activo) return;
      setProgramasConPruebas(Array.isArray(list) ? list : []);
    });
    return () => {
      activo = false;
    };
  }, [filtroAnio, filtroSemestre, filtroCompetencia]);

  const opcionesPrograma = useMemo(() => {
    if (!Array.isArray(programasConPruebas) || !programasConPruebas.length) return [];
    return [...programasConPruebas].sort((a, b) => String(a).localeCompare(String(b)));
  }, [programasConPruebas]);

  const { filas: filasTabla, totales: totalesTabla, totalEstudiantes, loading: loadingTabla } = useTablaResumen(
    filtroAnio,
    filtroSemestre,
    filtroPrograma,
    filtroCompetencia
  );

  const ultimoPeriodo = useMemo(() => {
    if (!Array.isArray(datos) || datos.length === 0) return null;
    const años = [...new Set(datos.map(item => Number(item.anio) || 0))].filter(Boolean).sort((a, b) => a - b);
    if (!años.length) return null;
    const ultimoAnio = años[años.length - 1];
    const semestres = [...new Set(datos.filter(item => Number(item.anio) === ultimoAnio).map(item => Number(item.semestre) || 0))]
      .filter(Boolean)
      .sort((a, b) => a - b);
    return {
      anio: String(ultimoAnio),
      semestre: semestres.length ? String(semestres[semestres.length - 1]) : "",
    };
  }, [datos]);

  const filtrosInicializados = useRef(false);
  const programaInicializado = useRef(false);

  useEffect(() => {
    if (!opcionesPrograma.length || programaInicializado.current) return;
    setFiltroPrograma((prev) => {
      if (!esFiltroTodos(prev)) {
        programaInicializado.current = true;
        return prev;
      }
      const defecto = opcionesPrograma.find((p) => normalize(p) === normalize(PROGRAMA_DEFAULT));
      programaInicializado.current = true;
      return defecto || opcionesPrograma[0] || FILTRO_TODOS;
    });
  }, [opcionesPrograma]);

  useEffect(() => {
    if (!ultimoPeriodo || filtrosInicializados.current) return;
    if (esFiltroTodos(filtroAnio) && ultimoPeriodo.anio) {
      setFiltroAnio(ultimoPeriodo.anio);
    }
    if (esFiltroTodos(filtroSemestre) && ultimoPeriodo.semestre) {
      setFiltroSemestre(ultimoPeriodo.semestre);
    }
    filtrosInicializados.current = true;
  }, [ultimoPeriodo, filtroAnio, filtroSemestre]);

  useEffect(() => {
    if (!esFiltroTodos(filtroSemestre) && opcionesSemestre.length && !opcionesSemestre.includes(filtroSemestre)) {
      setFiltroSemestre(FILTRO_TODOS);
    }
  }, [opcionesSemestre, filtroSemestre]);

  useEffect(() => {
    if (!opcionesPrograma.length) return;
    if (
      !esFiltroTodos(filtroPrograma) &&
      !opcionesPrograma.some((p) => normalize(p) === normalize(filtroPrograma))
    ) {
      const defecto = opcionesPrograma.find((p) => normalize(p) === normalize(PROGRAMA_DEFAULT));
      setFiltroPrograma(defecto || opcionesPrograma[0] || FILTRO_TODOS);
    }
  }, [opcionesPrograma, filtroPrograma]);

  const datosPeriodo = useMemo(() => {
    if (!Array.isArray(datos)) return [];
    return datos.filter(
      (item) =>
        coincidePeriodo(item, filtroAnio, filtroSemestre) && coincidePrograma(item, filtroPrograma)
    );
  }, [datos, filtroAnio, filtroSemestre, filtroPrograma]);

  const datosGrafica = useMemo(() => {
    if (!Array.isArray(datos)) return [];
    return datos
      .filter((item) => coincidePrograma(item, filtroPrograma))
      .sort((a, b) => {
        const yearA = Number(a.anio);
        const yearB = Number(b.anio);
        if (yearA !== yearB) return yearA - yearB;
        return Number(a.semestre) - Number(b.semestre);
      });
  }, [datos, filtroPrograma]);

  const periodosPrograma = useMemo(() => {
    if (!Array.isArray(datosGrafica) || datosGrafica.length === 0) return [];
    return [...new Set(datosGrafica.map(item => `${item.anio}-${item.semestre}`))].sort((a, b) => {
      const [yearA, semA] = a.split("-").map(Number);
      const [yearB, semB] = b.split("-").map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return semA - semB;
    });
  }, [datosGrafica]);

  const [selectedPeriodoAnterior, setSelectedPeriodoAnterior] = useState("");
  const [selectedPeriodoReciente, setSelectedPeriodoReciente] = useState("");

  const periodosAnterior = useMemo(() => {
    if (!Array.isArray(periodosPrograma) || periodosPrograma.length === 0) return [];
    const indexReciente = selectedPeriodoReciente ? periodosPrograma.indexOf(selectedPeriodoReciente) : periodosPrograma.length - 1;
    return indexReciente > 0 ? periodosPrograma.slice(0, indexReciente) : [];
  }, [periodosPrograma, selectedPeriodoReciente]);

  const periodosReciente = useMemo(() => {
    if (!Array.isArray(periodosPrograma) || periodosPrograma.length === 0) return [];
    const indexAnterior = selectedPeriodoAnterior ? periodosPrograma.indexOf(selectedPeriodoAnterior) : -1;
    return indexAnterior >= 0 ? periodosPrograma.slice(indexAnterior + 1) : periodosPrograma;
  }, [periodosPrograma, selectedPeriodoAnterior]);

  useEffect(() => {
    if (!Array.isArray(periodosPrograma) || periodosPrograma.length === 0) return;

    const latestPeriodo = periodosPrograma[periodosPrograma.length - 1];
    const previousPeriodo = periodosPrograma.length > 1 ? periodosPrograma[periodosPrograma.length - 2] : "";

    if (!selectedPeriodoReciente || !periodosPrograma.includes(selectedPeriodoReciente)) {
      setSelectedPeriodoReciente(latestPeriodo);
    }

    if (!selectedPeriodoAnterior || !periodosPrograma.includes(selectedPeriodoAnterior) || periodosPrograma.indexOf(selectedPeriodoAnterior) >= periodosPrograma.indexOf(latestPeriodo)) {
      setSelectedPeriodoAnterior(previousPeriodo);
    }
  }, [periodosPrograma, selectedPeriodoAnterior, selectedPeriodoReciente]);

  useEffect(() => {
    if (!selectedPeriodoReciente || !Array.isArray(periodosPrograma) || periodosPrograma.length === 0) return;
    const indexReciente = periodosPrograma.indexOf(selectedPeriodoReciente);
    if (indexReciente > 0) {
      const defaultAnterior = periodosPrograma[indexReciente - 1];
      if (!selectedPeriodoAnterior || periodosPrograma.indexOf(selectedPeriodoAnterior) >= indexReciente) {
        setSelectedPeriodoAnterior(defaultAnterior);
      }
    }
  }, [periodosPrograma, selectedPeriodoReciente, selectedPeriodoAnterior]);

  const calcularMejorCompetencia = (items) => {
    if (!Array.isArray(items) || !items.length) return null;
    // Subcompetencias dinámicas (RC/LC: C1-C3, CC: C1-C7): se descubren por
    // item en vez de asumir C1-C3.
    const competencias = [];
    items.forEach((item) => {
      codigosSubcompetencia(item).forEach((codigo) => {
        competencias.push({
          label: codigo,
          value: promedioSubcompetencia(item, codigo),
          text: etiquetaSubcompetencia(item, codigo),
        });
      });
    });
    if (!competencias.length) return null;
    return competencias.sort((a, b) => b.value - a.value)[0];
  };

  const metrics = useMemo(() => {
    if (!datosPeriodo.length) {
      return {
        promedio: 0,
        desviacion: 0,
        competenciaLabel: "",
      };
    }

    const promedio = datosPeriodo.length
      ? datosPeriodo.reduce((sum, item) => sum + Number(item.promedio_total || 0), 0) / datosPeriodo.length
      : 0;
    const desviacion = datosPeriodo.length
      ? Number(datosPeriodo[0].estand_total || 0)
      : 0;
    const competencias = calcularMejorCompetencia(datosPeriodo);
    return {
      promedio: Number.isFinite(promedio) ? promedio : 0,
      desviacion: Number.isFinite(desviacion) ? desviacion : 0,
      competenciaLabel: competencias ? competencias.text : "",
    };
  }, [datosPeriodo]);

  // Códigos y nombres de subcompetencia de la competencia seleccionada, tal
  // como los trae el backend (endpoint de competencias): así aparecen apenas
  // se elige la competencia, sin esperar a que carguen datos de resumen.
  const subcompetenciasCompetenciaActual = useMemo(() => {
    const actual = opcionesCompetencia.find((c) => c.codigo === filtroCompetencia);
    return actual?.subcompetencias || [];
  }, [opcionesCompetencia, filtroCompetencia]);

  const competenciasNombres = useMemo(() => {
    const reference =
      Array.isArray(datosGrafica) && datosGrafica.length > 0
        ? datosGrafica[datosGrafica.length - 1]
        : null;
    // El número de subcompetencias varía por competencia (RC/LC: 3, CC: 7):
    // se usan las que trae el backend para esta competencia; el promedio sí
    // depende de la fila de resumen más reciente.
    if (subcompetenciasCompetenciaActual.length) {
      return subcompetenciasCompetenciaActual.map(({ codigo, nombre }) => ({
        key: `${codigo}:`,
        name: nombre || codigo,
        value: reference ? promedioSubcompetencia(reference, codigo) : 0,
      }));
    }
    // Respaldo: si el backend todavía no expone subcompetencias para esta
    // competencia, se descubren desde los datos como antes.
    if (!reference) return [];
    return codigosSubcompetencia(reference).map((codigo) => ({
      key: `${codigo}:`,
      name: etiquetaSubcompetencia(reference, codigo),
      value: promedioSubcompetencia(reference, codigo),
    }));
  }, [datosGrafica, subcompetenciasCompetenciaActual]);

  const loadingVista = loadingResumen;

  const etiquetaFiltro = (valor) => (esFiltroTodos(valor) ? "todos" : String(valor));

  const descargarExcelResumen = async () => {
    if (descargaExcelEnCurso.current) return;
    descargaExcelEnCurso.current = true;
    setDescargandoExcel(true);
    try {
      const filasExport = await getExcelResumen({
        anio: filtroAnio,
        semestre: filtroSemestre,
        programa: filtroPrograma,
        competencia: filtroCompetencia,
      });

      if (!Array.isArray(filasExport) || !filasExport.length) return;

      // Mismo criterio que en toda la vista: se trunca (no redondea) al
      // primer decimal, para que la nota del Excel coincida con la que se ve
      // en pantalla.
      const toNumber = (valor) => truncarNota(valor) ?? 0;

      // Columnas de subcompetencia dinámicas (RC/LC: C1-C3, CC: C1-C7): se
      // descubren por fila en vez de asumir C1-C3.
      const codigosExport = codigosSubcompetencia(filasExport);

      const filasExcel = filasExport.map((item) => {
        const correo = (item.Correo || "").trim();
        const nombre = (item.estudiante_nombre || "").trim();
        const telefono = (item.estudiante_telefono || "").trim();
        const fila = {
          "Nombre estudiante": nombre,
          "Correo institucional": correo,
          "Teléfono": telefono,
          "Nota total": toNumber(item.Total),
        };
        codigosExport.forEach((codigo) => {
          fila[`Nota ${codigo}`] = toNumber(valorNormalizado(item, codigo));
        });
        return fila;
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(filasExcel);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Resumen");

      const nombreArchivo = `resumen_alertas_${etiquetaFiltro(filtroAnio)}_${etiquetaFiltro(filtroSemestre)}_${etiquetaFiltro(filtroPrograma).replace(/\s+/g, "_").replace(/[^\w\-]/g, "")}.xlsx`;
      XLSX.writeFile(workbook, nombreArchivo, { bookType: "xlsx", compression: true });
    } finally {
      descargaExcelEnCurso.current = false;
      setDescargandoExcel(false);
    }
  };

  if (loadingVista) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="360px" pb={6}>
        <TareasTerminadas mensaje="Cargando..." imagen={emma_w} widthImg="7%" cargando />
      </Box>
    );
  }

  return (
    <Box pb={0.5} pt={0.25} style={{ maxWidth: 1360, margin: "0 auto", paddingLeft: 12, paddingRight: 12 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" gap={1}>
        <Typography variant="h6" component="h1" style={{ fontWeight: 800, color: "#0f172a" }}>
          Estadísticas de Desempeño
        </Typography>
        <FiltrosResumen
          filtroAnio={filtroAnio}
          filtroSemestre={filtroSemestre}
          filtroPrograma={filtroPrograma}
          filtroCompetencia={filtroCompetencia}
          opcionesAnio={opcionesAnio}
          opcionesSemestre={opcionesSemestre}
          opcionesPrograma={opcionesPrograma}
          opcionesCompetencia={opcionesCompetencia}
          onAnioChange={setFiltroAnio}
          onSemestreChange={setFiltroSemestre}
          onProgramaChange={setFiltroPrograma}
          onCompetenciaChange={setFiltroCompetencia}
          onDescargarExcel={descargarExcelResumen}
          deshabilitarDescarga={descargandoExcel}
        />
      </Box>

      <Grid container spacing={1} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Box mb={0.75}>
            <TarjetasResumen metrics={metrics} seleccionado={datosPeriodo.length > 0} />
          </Box>
          <Box mt={1.25} mb={1}>
            <GraficaResumen datos={datosGrafica} height={186} />
          </Box>
          {!esFiltroTodos(filtroPrograma) && periodosPrograma.length > 1 && (
            <Box mt={1}>
              <Box bgcolor="#ffffff" borderRadius={14} boxShadow="0 1px 8px rgba(0, 0, 0, 0.08)" p={1.5}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  mb={1}
                  style={{ gap: 12 }}
                >
                  <Box flex="1 1 200px" minWidth={0} style={{ maxWidth: "100%" }}>
                    <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0f172a" }}>
                      Rendimiento temporal
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" style={{ fontSize: 11, marginTop: 2 }}>
                      Competencias del programa entre dos periodos.
                    </Typography>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 14, marginTop: 6 }}>
                      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#0f172a",
                            border: "1px solid #0f172a",
                            display: "inline-block",
                          }}
                        />
                        <Typography variant="caption" style={{ color: "#334155", fontWeight: 600 }}>
                          Anterior
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: "#1976d2",
                            border: "1px solid rgba(255,255,255,0.6)",
                            display: "inline-block",
                          }}
                        />
                        <Typography variant="caption" style={{ color: "#334155", fontWeight: 600 }}>
                          Reciente
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" flexShrink={0}>
                   <FormControl
                    variant="outlined"
                    size="small"
                    style={{
                      minWidth: 140,
                      marginRight: 8,
                    }}
                  >
                    <InputLabel style={{ fontSize: 11 }}>
                      Periodo anterior
                    </InputLabel>
                    <Select
                      value={selectedPeriodoAnterior}
                      onChange={(e) => setSelectedPeriodoAnterior(e.target.value)}
                      label="Periodo anterior"
                      style={{
                        height: 34,
                        fontSize: 12,
                      }}
                    >
                        {periodosAnterior.map((periodo) => (
                          <MenuItem key={periodo} value={periodo} style={{ fontSize: 13 }}>
                            {periodo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                        variant="outlined"
                        size="small"
                        style={{
                          minWidth: 140,
                          marginRight: 8,
                        }}
                        >
                        <InputLabel style={{ fontSize: 11 }}>
                          Periodo reciente
                        </InputLabel>
                        <Select
                          value={selectedPeriodoReciente}
                          onChange={(e) => setSelectedPeriodoReciente(e.target.value)}
                          label="Periodo reciente"
                          style={{
                            height: 34,
                            fontSize: 12,
                          }}
                        >
                        {periodosReciente.map((periodo) => (
                          <MenuItem key={periodo} value={periodo} style={{ fontSize: 13 }}>
                            {periodo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <Box mt={0.5}>
                  <GraficaComparativa
                    datos={datosGrafica}
                    periodoAnterior={selectedPeriodoAnterior}
                    periodoReciente={selectedPeriodoReciente}
                    height={194}
                  />
                </Box>
              </Box>
            </Box>
          )}
          {competenciasNombres.length > 0 && (
            // auto-fit en vez de 3 columnas fijas: con RC/LC (3 tarjetas) se
            // ven anchas y parejas; con CC/IN (7 tarjetas) reflowan solas en
            // vez de dejar una última fila con una sola tarjeta huérfana.
            <Box
              mt={1.5}
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))"
              style={{ gap: 12 }}
            >
              {competenciasNombres.map((comp, index) => (
                <Card
                  key={comp.key}
                  elevation={2}
                  style={{
                    borderRadius: 12,
                    padding: "10px 12px",
                    minHeight: 64,
                    background: "#ffffff",
                  }}
                >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    style={{
                      flexShrink: 0,
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      background: "#eef2ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {iconoParaCodigo(comp.key.replace(":", ""), index)}
                  </Box>
                  <Box display="flex" flexDirection="column" style={{ minWidth: 0 }}>
                    <Typography variant="caption" style={{ fontWeight: 800, color: "#0f172a" }}>
                      {comp.key}
                    </Typography>

                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ fontSize: 11, lineHeight: 1.3, wordBreak: "break-word" }}
                    >
                      {comp.name}
                    </Typography>
                  </Box>

                </Box>
              </Card>
              ))}
            </Box>
          )}
        </Grid>

        <Grid item xs={12} md={6} style={{ paddingLeft: 12, display: "flex" }}>
          <TablaResumen
            filas={filasTabla}
            totales={totalesTabla}
            totalEstudiantes={totalEstudiantes}
            loading={loadingTabla}
          />
        </Grid>
      </Grid>
      <Dialog
        open={descargandoExcel}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        aria-labelledby="descargando-excel-alertas"
      >
        <AppBarModal
          titulo="Descarga en proceso"
          mostrarModal={() => {}}
          titulo_accion=""
          accion=""
        />
        <DialogContent className="scroll" style={{ paddingTop: 16, paddingBottom: 16 }}>
          <TareasTerminadas
            mensaje="Descargando Excel"
            imagen={emma_w}
            widthImg="7%"
            marginTop="0"
            cargando
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Resumen;